import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { StateService } from 'src/app/auth/state.service';
import { DataRequestState } from 'src/app/data-request/model';
import { toRequestState } from 'src/app/data-request/operators';
import { DialogSize } from 'src/app/models_new/enums/dialogSize';
import {
  IComment,
  ICommentActionEvent,
} from 'src/app/models_new/types/comment';
import { IHasuraUser } from 'src/app/models_new/types/hasura-user';
import { Role } from 'src/app/models_new/types/role';
import {
  CommentApiService,
  SimulationType,
} from 'src/app/services/api/comment-api.service';
import { RoleApiService } from 'src/app/services/api/role-api.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics-service.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ObjectUtils } from 'src/app/utils/object';
import { environment } from 'src/environments/environment';
import { CaptchaDialogComponent } from '../../dialogs/captcha-dialog/captcha-dialog.component';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
})
export class CommentsComponent implements OnInit, OnDestroy {
  commentsDataRequest$: Observable<DataRequestState<IComment[]>>;
  private commentsData: IComment[];
  displayedComments: IComment[] = [];

  userLoggedType$: Observable<
    string | 'org_admin' | 'user' | 'public' | 'rf_backoffice'
  > = of('public');

  @Input() accessMode?: SimulationType = SimulationType.PUBLIC;
  @Input() user: IHasuraUser;
  @Input() lastActionTimestamp: number;
  @Input() simulationId: string;
  @Input() simulationOrgId: string;
  @Output() actionPerformed?: EventEmitter<boolean> = new EventEmitter();

  destroy$ = new Subject<undefined>();

  constructor(
    private commentApiService: CommentApiService,
    public route: ActivatedRoute,
    private errorHandler: ErrorHandlerService,
    private dialogService: DialogService,
    private notify: NotificationService,
    public stateService: StateService,
    private gas: GoogleAnalyticsService,
    private roleService: RoleApiService
  ) {}

  ngOnInit(): void {
    this.commentsDataRequest$ = this.commentApiService
      .getComments(this.simulationId, this.accessMode)
      .pipe(
        takeUntil(this.destroy$),
        map((comments) => {
          this.commentsData = ObjectUtils.cloneObject(
            this.updateComments(comments)
          );
          this.displayedComments = this.nestComments(this.commentsData);
          return this.commentsData;
        }),
        toRequestState()
      );

    if (this.user && this.accessMode === SimulationType.PRIVATE) {
      const backofficeOrgs = environment.backofficeOrgs;
      this.userLoggedType$ = this.roleService
        //Getting all user roles for the simulation Organization and Backoffice Orgs.
        .getUserRoles([this.simulationOrgId, ...backofficeOrgs], this.user.id)
        .pipe(
          map((orgRoles) => {
            if (orgRoles?.includes('rf_backoffice')) return 'rf_backoffice';
            else if (orgRoles?.includes('org_admin')) return 'org_admin';
            else return 'user';
          })
        );
    }
  }

  ngOnDestroy() {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  private updateComments(commentsUpdate: IComment[]) {
    commentsUpdate.forEach((comment) => {
      const storedCommentState = this.commentsData?.find(
        (c) => c.id === comment.id
      );
      if (storedCommentState) {
        comment.children_expanded = storedCommentState.children_expanded;
        comment.reply_expanded = storedCommentState.reply_expanded;
      }
    });
    return commentsUpdate;
  }

  /**
   * Returns a formatted/nested list of comments, with subcomments nested inside every parent comment object.
   * @param comments
   * @returns
   */
  private nestComments(comments: IComment[]): IComment[] {
    for (const comment of comments) {
      if (comment.parent_comment !== null) {
        const parentComment = comments.find(
          (c) => c.id === comment.parent_comment
        );
        if (parentComment)
          parentComment.children?.length
            ? parentComment.children.push(comment)
            : (parentComment.children = [comment]);
      }
    }
    return comments.filter((c) => !c.parent_comment);
  }

  /**
   * Handles every possible comment-action.
   * @param response ICommentActionEvent. Event type to be triggered [POST | UPDATE | DELETE]
   */
  public doActionComment(response: ICommentActionEvent): void {
    switch (response.event) {
      case 'post':
        if (response.comment_reply.comment) {
          this.captchaCheck(response.comment_reply);
          response.comment_reply.parent_id
            ? this.gas.addEvent(
                'Simulation Details - MyRobot.cloud	Reply to a Comment - Click',
                null,
                this.gas.getLastParam()
              )
            : this.gas.addEvent(
                'Simulation Details - MyRobot.cloud	Add Comment - Click',
                null,
                this.gas.getLastParam()
              );
        } else {
          this.errorHandler.handleError(new Error('Comment missing...'));
        }
        break;
      case 'update':
        if (response.comment_reply.comment) {
          this.doUpdateComment(
            response.comment_id,
            response.comment_reply.comment
          );
        } else {
          this.errorHandler.handleError(new Error('Comment missing...'));
        }
        break;
      case 'delete':
        const dialogRef = this.dialogService.showStandardDialog({
          content: {
            title: 'Delete comment',
            contentAsString: 'Are you sure? This can NOT be reversed!',
          },
          button: { text: 'Delete', type: 'delete' },
          gui: { size: DialogSize.SMALL },
        });
        dialogRef
          .afterClosed()
          .pipe(take(1), filter(Boolean))
          .subscribe(() => {
            this.doDeleteComment(response.comment_id, response.role);
          });
        break;
      case 'expand':
      case 'reply':
        this.toggleCommentState(response.comment_id, response.event);
        break;
      default:
        console.warn(`Action <${response.event}> unknown.`);
        break;
    }
  }

  /**
   * Display a captcha challange if there isn't any user logged-in before proceeding.
   * @param reply IcommentActionEvent.comment_reply object type.
   */
  private captchaCheck(reply: ICommentActionEvent['comment_reply']): void {
    if (!this.user && this.accessMode !== SimulationType.PRIVATE)
      this.dialogService
        .showCustomDialog(
          CaptchaDialogComponent,
          DialogSize.SMALL,
          null,
          null,
          true
        )
        .afterClosed()
        .subscribe((validated) => {
          if (validated) {
            this.doPostComment(reply);
          } else {
            this.notify.showError(`Couldn't verify you are human`);
          }
        });
    else this.doPostComment(reply);
  }

  /**
   * Inserts new comments and comment replies.
   * @param reply IcommentActionEvent.comment_reply object type.
   */
  private doPostComment(reply: ICommentActionEvent['comment_reply']): void {
    if (Date.now() - this.lastActionTimestamp > 5000 || this.user) {
      reply.comment?.replace(/["'`]+/g, '');
      const metadata: IComment['metadata'] =
        reply.public_user && !this.user
          ? {
              user: {
                name: reply.public_user.name || null,
                organization: reply.public_user.organization || null,
                job_title: reply.public_user.job_title || null,
                picture: reply.public_user.picture || null,
              },
              isPublicComment: true,
            }
          : {
              user: {
                name: this.user.name,
                organization: this.user.metadata?.organization,
                job_title: this.user.metadata?.job_title,
                picture: this.user.metadata?.picture,
              },
            };
      this.commentApiService
        .insertComment(reply, this.simulationId, this.accessMode, metadata)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.actionPerformed.emit(true);
            this.notify.showSuccess('New comment added!');
          },
          error: (err) => this.errorHandler.handleError(err),
        });
    } else {
      this.errorHandler.logError(new Error(`Couldn't verify you are human`));
    }
  }

  /**
   * Updates comments. Allowed to owner_id matching users.
   * @param id
   * @param comment
   */
  private doUpdateComment(id: string, comment: string): void {
    comment.replace(/["'`]+/g, '');
    this.commentApiService.updateCommentById(id, comment).subscribe({
      next: () => {
        this.actionPerformed.emit(true);
        this.notify.showSuccess('Comment successfully edited');
      },
      error: (err) => this.errorHandler.handleError(err),
    });
  }

  /**
   * Deletes comment by Id.
   * Only allowed to org_admin, rf_backoffice and owner_id matching user.
   * @param id
   */
  private doDeleteComment(id: string, role?: Role): void {
    this.commentApiService.deleteCommentById(id, role).subscribe({
      next: () => {
        this.actionPerformed.emit(true);
        this.notify.showSuccess('Comment deleted');
      },
      error: (err) => this.errorHandler.handleError(err),
    });
  }

  /**
   * Deletes comment by Id.
   * Only allowed to org_admin and owner_id matching user.
   * @param id
   */
  private toggleCommentState(comment_id: string, event: 'reply' | 'expand') {
    if (event === 'expand')
      this.commentsData.find(
        (comment) => comment.id === comment_id
      ).children_expanded = !this.commentsData.find(
        (comment) => comment.id === comment_id
      ).children_expanded;
    else if (event === 'reply') {
      this.commentsData.find(
        (comment) => comment.id === comment_id
      ).reply_expanded = !this.commentsData.find(
        (comment) => comment.id === comment_id
      ).reply_expanded;
    }
  }
}
